import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Output,
  QueryList,
  ViewChildren,
  forwardRef,
  input,
  computed,
  signal,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'zef-code-input',
  templateUrl: './code-input.component.html',
  styleUrls: ['./code-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CodeInputComponent),
      multi: true,
    },
  ],
  imports: [],
})
export class CodeInputComponent implements AfterViewInit, ControlValueAccessor {
  length = input(6);
  placeholder = input('-');
  disabled = input(false);
  autoFocus = input(true);

  @Output()
  readonly valueChange = new EventEmitter<string>();

  @Output()
  readonly complete = new EventEmitter<string>();

  @Output()
  readonly submit = new EventEmitter<string>();

  @ViewChildren('input')
  inputs?: QueryList<ElementRef<HTMLInputElement>>;

  protected items = computed(() => Array.from({ length: this.length() }));

  // Internal disabled state for ControlValueAccessor
  private internalDisabled = signal(false);

  // Computed property that combines input disabled and internal disabled
  protected isDisabled = computed(() => this.disabled() || this.internalDisabled());

  private value = '';
  private onChange?: (value: string) => void;
  private onTouched?: () => void;
  private afterInput?: () => void;

  ngAfterViewInit(): void {
    if (this.autoFocus()) {
      this.inputs?.first.nativeElement.focus();
    }
  }

  writeValue(value: string): void {
    if (value !== this.value) {
      this.value = value || '';
      this.updateInputsFromValue();
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.internalDisabled.set(isDisabled);
  }

  protected onInput(event: InputEvent, index: number): void {
    if (this.isDisabled()) {
      return;
    }

    if (event.inputType === 'insertCompositionText' || event.inputType === 'insertText') {
      const data = this.getInputFromData(event.data);
      this.afterInput = () => this.setInputsFromData(data, index);
    }

    if (event.target instanceof HTMLInputElement) {
      // Clear the input - we'll set the correct value in afterInput
      event.target.value = '';
    }

    this.afterInput?.();
    this.afterInput = undefined;
  }

  protected onKeydown(event: KeyboardEvent, index: number): void {
    if (this.isDisabled()) {
      return;
    }

    const inputs = this.inputs?.toArray() || [];
    const input = inputs[index]?.nativeElement;

    if (event.key === 'Enter') {
      event.preventDefault();
      if (this.value.length === this.length()) {
        this.submit.emit(this.value);
      }
    } else if (event.key === 'Backspace' || event.key === 'Delete') {
      event.preventDefault();
      input.value = '';
      this.updateValue();

      // Move to previous input on backspace
      if (event.key === 'Backspace' && index > 0) {
        inputs[index - 1]?.nativeElement.focus();
      }
    } else if (event.ctrlKey || event.altKey || event.metaKey) {
      return;
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      inputs[index - 1]?.nativeElement.focus();
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      inputs[index + 1]?.nativeElement.focus();
    } else if (/^[a-zA-Z0-9]$/.test(event.key)) {
      // Allow only alphanumeric characters
      input.value = event.key.toUpperCase();
      this.updateValue();

      // Move to next input
      if (index < inputs.length - 1) {
        inputs[index + 1]?.nativeElement.focus();
      }

      event.preventDefault();
    } else if (event.key !== 'Tab' && event.key !== 'Shift') {
      // Prevent other characters
      event.preventDefault();
    }
  }

  protected onPaste(event: ClipboardEvent, index: number): void {
    if (this.isDisabled()) {
      return;
    }

    event.preventDefault();
    const data = this.getInputFromData(event.clipboardData?.getData('text/plain'));
    this.setInputsFromData(data, index);
  }

  protected onFocus(): void {
    if (this.isDisabled()) {
      return;
    }
    this.onTouched?.();
  }

  private getInputFromData(data?: string): string[] {
    return (
      data
        ?.replace(/[^a-zA-Z0-9]/g, '')
        .toUpperCase()
        .split('') || []
    );
  }

  private setInputsFromData(data: string[], startIndex: number): void {
    if (data.length && this.inputs) {
      const inputs = this.inputs.toArray();
      const maxLength = Math.min(data.length, inputs.length - startIndex);

      data.slice(0, maxLength).forEach((char, i) => {
        if (inputs[startIndex + i]) {
          inputs[startIndex + i].nativeElement.value = char;
        }
      });

      // Focus next empty input or last input
      const nextIndex = Math.min(startIndex + maxLength, inputs.length - 1);
      inputs[nextIndex]?.nativeElement.focus();

      this.updateValue();
    }
  }

  private updateInputsFromValue(): void {
    if (this.inputs) {
      const inputs = this.inputs.toArray();
      const chars = this.value.split('');

      inputs.forEach((input, index) => {
        input.nativeElement.value = chars[index] || '';
      });
    }
  }

  private updateValue(): void {
    if (this.inputs) {
      const newValue = this.inputs
        .toArray()
        .map((input) => input.nativeElement.value)
        .join('');

      if (newValue !== this.value) {
        this.value = newValue;
        this.onChange?.(this.value);
        this.valueChange.emit(this.value);

        // Emit complete event when all inputs are filled
        if (this.value.length === this.length()) {
          this.complete.emit(this.value);
        }
      }
    }
  }

  focus(): void {
    // Focus the first empty input or the first input
    const inputs = this.inputs?.toArray() || [];
    const firstEmpty = inputs.find((input) => !input.nativeElement.value);
    (firstEmpty || inputs[0])?.nativeElement.focus();
  }

  clear(): void {
    if (this.inputs) {
      this.inputs.forEach((input) => {
        input.nativeElement.value = '';
      });
      this.value = '';
      this.onChange?.('');
      this.valueChange.emit('');
      this.inputs.first?.nativeElement.focus();
    }
  }
}
